import React, { useRef, useState } from "react";
import API_BASE_URL from "../config";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { Button, Container, Message, Segment } from "semantic-ui-react";
import KvkkModal from "../components/KvkkModal";

import Layout from "../components/Layout";
import Info from "../components/Info";
import Main from "../components/Main";
import Quiz from "../components/Quiz";
import Result from "../components/Result";
import TieBreaker from "../components/TieBreaker";
import Wing from "../components/Wing";
import Loader from "../components/Loader";
import { QUESTIONS_PER_PAGE } from "../constants/qtestTexts";

const errorMessage = (err, fallback) => {
  if (err?.response?.status === 429) {
    return err.response.data?.error || "Bu şirket için kullanım limiti dolmuştur.";
  }
  return err?.response?.data?.error || fallback;
};

const TestPage = () => {
  const [showKvkkModal, setShowKvkkModal] = useState(true);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [mainQuestions, setMainQuestions] = useState([]);
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [step, setStep] = useState(null); // "tiebreaker" | "wing" | null
  const [stepOptions, setStepOptions] = useState([]);
  const [isInfoStep, setIsInfoStep] = useState(true);
  const [resultData, setResultData] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [tempResultData, setTempResultData] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [flowError, setFlowError] = useState(null); // { message, retry }

  // Tekrar denemede oturumun ikinci kez açılmaması için (sessionId: sunucunun ürettiği oturum kimliği)
  const progressRef = useRef({ sessionCreated: false, responsesSaved: false, sessionId: null });

  const proceedToForm = () => setIsInfoStep(false);

  const startQuiz = (questions, userDetails) => {
    setLoading(true);
    setLoadingMessage({
      title: "Test hazırlanıyor...",
      message: "Lütfen bekleyin",
    });

    // Sunucu sırayı her istekte karıştırır; gelen sıra korunup 10'arlı sayfalara bölünür
    const pages = [];
    for (let i = 0; i < questions.length; i += QUESTIONS_PER_PAGE) {
      pages.push({
        question: `Sayfa ${pages.length + 1}`,
        options: questions.slice(i, i + QUESTIONS_PER_PAGE),
      });
    }

    setTimeout(() => {
      setMainQuestions(pages);
      setUserInfo(userDetails);
      setIsQuizStarted(true);
      setLoading(false);
    }, 1000);
  };

  const showError = (message, retry) => {
    setFlowError({ message, retry });
    setLoading(false);
  };

  const finishFlow = async (sid, quizResult = tempResultData) => {
    setFlowError(null);
    setLoading(true);
    setLoadingMessage({
      title: "Sonuç hesaplanıyor...",
      message: "Kişilik tipin belirleniyor",
    });

    let res;
    try {
      res = await axios.get(`${API_BASE_URL}/api/result/${sid}/`);
    } catch (err) {
      showError(errorMessage(err, "Sonuç alınamadı."), () => finishFlow(sid, quizResult));
      return;
    }

    setResultData({
      ...(quizResult ?? {}),
      resultType: res.data.result,
      resultName: res.data.result_name,
      wing: res.data.wing,
      reportUrl: res.data.report_url, // imzalı, süreli kişisel PDF adresi
    });
    setStep(null);
    setIsQuizCompleted(true);
    setLoading(false);

    // Mail hatası sonuç ekranını engellemez
    try {
      await axios.post(`${API_BASE_URL}/api/send-email/`, { session_id: sid });
      setEmailSent(true);
    } catch (err) {
      console.error("Mail gönderme hatası:", err);
    }
  };

  const goToNextStep = async (sid, quizResult = tempResultData) => {
    setFlowError(null);
    setLoading(true);
    setLoadingMessage({
      title: "Cevaplar değerlendiriliyor...",
      message: "Lütfen bekleyin",
    });

    let res;
    try {
      res = await axios.get(`${API_BASE_URL}/api/next-step/${sid}/`);
    } catch (err) {
      showError(errorMessage(err, "Bir sonraki adım alınamadı."), () => goToNextStep(sid, quizResult));
      return;
    }

    if (res.data.step === "done") {
      finishFlow(sid, quizResult);
      return;
    }
    setStepOptions(res.data.options || []);
    setStep(res.data.step);
    setLoading(false);
  };

  const submitQuiz = async (result, attemptKey) => {
    setFlowError(null);
    setLoading(true);
    setLoadingMessage({
      title: "Cevaplar kaydediliyor...",
      message: "Lütfen bekleyin",
    });

    let sid = progressRef.current.sessionId;
    try {
      if (!progressRef.current.sessionCreated) {
        // Oturum kimliğini sunucu üretir; aynı deneme anahtarıyla tekrar gönderimde mevcut oturum döner
        const res = await axios.post(`${API_BASE_URL}/api/session/`, {
          attempt_key: attemptKey,
          full_name: userInfo.full_name,
          email: userInfo.email,
          phone_number: userInfo.phone_number,
          company: userInfo.company,
          role: userInfo.role,
          token: userInfo.token, // ← Token'ı da ekliyoruz
        });
        sid = res.data.session_id;
        progressRef.current.sessionId = sid;
        progressRef.current.sessionCreated = true;
        setSessionId(sid);
      }

      if (!progressRef.current.responsesSaved) {
        const answers = result.questionsAndAnswers.flatMap((q) =>
          q.selectedOptionIds.map((optionId) => ({ option_id: optionId })),
        );
        await axios.post(`${API_BASE_URL}/api/response/`, {
          session_id: sid,
          answers,
        });
        progressRef.current.responsesSaved = true;
      }
    } catch (err) {
      console.error("Cevap gönderme hatası:", err);
      // Limit dolu (429) veya tekrar çözme izni yok (409): yeniden denemek sonucu değiştirmez
      const cannotRetry = [409, 429].includes(err?.response?.status);
      showError(
        errorMessage(err, "Cevaplarınız kaydedilemedi. Lütfen tekrar deneyin."),
        cannotRetry ? null : () => submitQuiz(result, attemptKey),
      );
      return;
    }

    setIsQuizStarted(false);
    goToNextStep(sid, result);
  };

  const endQuiz = (result) => {
    // Her test denemesi için yeni deneme anahtarı (yalnızca tekrar gönderimi önlemek için)
    const attemptKey = uuidv4();
    progressRef.current = { sessionCreated: false, responsesSaved: false, sessionId: null };
    setSessionId(null);
    setTempResultData(result);
    submitQuiz(result, attemptKey);
  };

  const resetQuiz = () => {
    setLoading(true);
    setLoadingMessage({
      title: "Anasayfaya dönülüyor...",
      message: "Teşekkür ederiz!",
    });

    setTimeout(() => {
      setMainQuestions([]);
      setIsQuizStarted(false);
      setIsQuizCompleted(false);
      setStep(null);
      setStepOptions([]);
      setResultData(null);
      setUserInfo(null);
      setTempResultData(null);
      setSessionId(null);
      setEmailSent(false);
      setFlowError(null);
      progressRef.current = { sessionCreated: false, responsesSaved: false, sessionId: null };
      setIsInfoStep(true);
      setLoading(false);
    }, 1000);
  };

  return (
    <Layout>
      {loading && <Loader {...loadingMessage} />}

      {!loading && flowError && (
        <Container>
          <Segment>
            <Message negative>
              <Message.Header>İşlem tamamlanamadı</Message.Header>
              <p>{flowError.message}</p>
            </Message>
            {flowError.retry ? (
              <Button primary onClick={flowError.retry}>
                Tekrar Dene
              </Button>
            ) : (
              <Button onClick={resetQuiz}>Anasayfa</Button>
            )}
          </Segment>
        </Container>
      )}

      {!loading && !flowError && isInfoStep && <Info onNext={proceedToForm} />}

      {!loading &&
        !flowError &&
        !isInfoStep &&
        !isQuizStarted &&
        !isQuizCompleted &&
        !step && <Main startQuiz={startQuiz} />}

      {!loading && !flowError && isQuizStarted && mainQuestions.length > 0 && (
        <Quiz data={mainQuestions} endQuiz={endQuiz} userInfo={userInfo} />
      )}

      {!loading && !flowError && step === "tiebreaker" && sessionId && (
        <TieBreaker
          options={stepOptions}
          sessionId={sessionId}
          onComplete={() => goToNextStep(sessionId)}
          userInfo={userInfo}
        />
      )}

      {!loading && !flowError && step === "wing" && sessionId && (
        <Wing
          options={stepOptions}
          sessionId={sessionId}
          onComplete={() => finishFlow(sessionId)}
          userInfo={userInfo}
        />
      )}

      {!loading && !flowError && isQuizCompleted && resultData && (
        <Result
          questionsAndAnswers={resultData.questionsAndAnswers}
          replayQuiz={resetQuiz}
          resetQuiz={resetQuiz}
          totalQuestions={resultData.totalQuestions}
          resultType={resultData.resultType}
          resultName={resultData.resultName}
          wing={resultData.wing}
          reportUrl={resultData.reportUrl}
          emailSent={emailSent}
          userInfo={userInfo}
        />
      )}

      <KvkkModal
        open={showKvkkModal}
        onAccept={() => {
          setKvkkAccepted(true);
          setShowKvkkModal(false);
        }}
        onReject={() => {
          navigate("/");
        }}
      />
    </Layout>
  );
};

export default TestPage;
